/**
 * A geometria do cadastro de cercas — pura, para ser testada sem navegador.
 *
 * ## A troca de [lon, lat] é o erro desta tela
 *
 * O GeoJSON (e a coluna `geofences.polygon`) guarda **[longitude, latitude]**;
 * o Leaflet quer **[latitude, longitude]**. Trocar os dois **não dá erro**: o
 * polígono desenha igual, a API aceita, o banco guarda — e a cerca vai parar do
 * outro lado do mundo e nunca dispara.
 *
 * Por isso as duas conversões moram aqui, nomeadas pelo que produzem
 * (`toGeoJson`, `toLeaflet`), e têm teste. Em nenhum outro lugar do painel se
 * monta um par de coordenada de cerca à mão.
 *
 * ## O retângulo não é uma terceira geometria
 *
 * O banco só conhece `circle` e `polygon` (CHECK da 0001). Um retângulo é um
 * polígono de quatro vértices — é **ferramenta de desenho**, não forma. Guardar
 * "rectangle" exigiria uma migration, um valor novo no CHECK e um caso novo em
 * todo lugar que lê cerca, para desenhar exatamente o mesmo quadrilátero.
 */

/** Par como o Leaflet quer: [lat, lon]. */
export type LatLng = [number, number];
/** Par como o GeoJSON guarda: [lon, lat]. */
export type LonLat = [number, number];

/** Os tipos de cerca, com o rótulo que vai para a tela. */
export const PURPOSES = [
  { value: "point", label: "Ponto" },
  { value: "terminal", label: "Terminal" },
  { value: "garage", label: "Garagem" },
  { value: "route", label: "Via" },
  { value: "other", label: "Outro" },
] as const;

export type GeofencePurpose = (typeof PURPOSES)[number]["value"];

export function purposeLabel(purpose: string | null | undefined): string {
  return PURPOSES.find((p) => p.value === purpose)?.label ?? "Outro";
}

/** Cor padrão quando a cerca não tem uma. A mesma do `GeofenceLayer`. */
export const DEFAULT_COLOR = "#8b5cf6";

/** `#RRGGBB` — a mesma forma que o CHECK da migration 0026 aceita. */
export const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;

export function isHexColor(value: string): boolean {
  return HEX_COLOR.test(value);
}

/**
 * Normaliza o que o usuário digitou num `#RRGGBB`, ou devolve `null`.
 *
 * Aceita sem `#` e em qualquer caixa, porque é isso que alguém cola de um
 * catálogo de cores. **Não** aceita a forma curta `#abc`: ela é válida em CSS e
 * seria uma segunda forma de escrever a mesma cor, obrigando a normalizar antes
 * de comparar duas — ver o comentário da 0026.
 */
export function normalizeHex(value: string): string | null {
  const limpo = value.trim();
  const comHash = limpo.startsWith("#") ? limpo : `#${limpo}`;
  return HEX_COLOR.test(comHash) ? comHash.toLowerCase() : null;
}

/**
 * Os vértices guardados, em coordenadas do Leaflet.
 *
 * **A API grava e devolve um ARRAY PURO de `[lon, lat]`** — é o que o
 * `geofences.controller.ts` serializa (`JSON.stringify(points)`) e o que o
 * processor lê (`Array.isArray(row.polygon)`).
 *
 * Até 02/11 esta função esperava `{ type: "Polygon", coordinates: [anel] }`, o
 * formato do GeoJSON completo, que **ninguém nunca gravou**. Ela devolvia
 * `null` para toda cerca de polígono, e o mapa simplesmente não desenhava
 * nenhuma. Passou despercebido porque `geofenceSchema.polygon` era
 * `z.unknown()` — a borda não reclamava — e porque o teste desta função
 * construía o formato embrulhado, concordando com o engano. É a `MEMORY.md`
 * §17: fixture montada a partir do que se ACHA que a API manda.
 *
 * `unknown` na entrada continua de propósito: o esquema já garante a forma, e
 * uma segunda rede aqui custa três linhas e evita o mapa quebrar inteiro por
 * uma cerca torta.
 */
export function toLeaflet(polygon: unknown): LatLng[] | null {
  if (!Array.isArray(polygon)) return null;
  const pontos: LatLng[] = [];
  for (const par of polygon) {
    if (!Array.isArray(par) || par.length < 2) continue;
    const [lon, lat] = par as [number, number];
    if (typeof lat === "number" && typeof lon === "number") pontos.push([lat, lon]);
  }
  return pontos.length >= 3 ? pontos : null;
}

/**
 * Os vértices no formato que a API recebe: `[lon, lat]`, sem fechar o anel.
 *
 * A API fecha sozinha. Mandar o primeiro ponto repetido no fim faria um vértice
 * a mais em cada salvamento — e depois de três edições o polígono teria três
 * pontos empilhados no mesmo lugar.
 */
export function toGeoJson(points: readonly LatLng[]): LonLat[] {
  const abertos = dropClosingPoint(points);
  return abertos.map(([lat, lon]) => [lon, lat] as LonLat);
}

/**
 * O traçado guardado, em coordenadas do Leaflet.
 *
 * Igual ao `toLeaflet`, com UMA diferença: aceita dois vértices. O polígono
 * exige três porque com dois não tem área; a linha com dois é um trecho reto de
 * via, que é o caso mais comum. Usar a função do polígono aqui devolveria
 * `null` e a cerca reabriria vazia no cadastro.
 */
export function toLeafletLine(polygon: unknown): LatLng[] | null {
  if (!Array.isArray(polygon)) return null;
  const pontos: LatLng[] = [];
  for (const par of polygon) {
    if (!Array.isArray(par) || par.length < 2) continue;
    const [lon, lat] = par as [number, number];
    if (typeof lat === "number" && typeof lon === "number") pontos.push([lat, lon]);
  }
  return pontos.length >= 2 ? pontos : null;
}

/**
 * Os vértices do traçado no formato da API: `[lon, lat]`, SEM tirar o último.
 *
 * O `toGeoJson` descarta o ponto final quando ele repete o primeiro, porque no
 * polígono isso é o fechamento do anel. Numa LINHA não é: uma via que termina
 * onde começou — uma volta de itinerário — perderia o último trecho em cada
 * salvamento, e o traçado iria encolhendo a cada edição.
 */
export function toGeoJsonLine(points: readonly LatLng[]): LonLat[] {
  return points.map(([lat, lon]) => [lon, lat] as LonLat);
}

/** Tira o último ponto quando ele repete o primeiro. */
export function dropClosingPoint(points: readonly LatLng[]): LatLng[] {
  const lista = [...points];
  const primeiro = lista[0];
  const ultimo = lista.at(-1);
  if (lista.length > 3 && primeiro && ultimo && primeiro[0] === ultimo[0] && primeiro[1] === ultimo[1]) {
    lista.pop();
  }
  return lista;
}

/**
 * O retângulo com dois cantos opostos, como polígono.
 *
 * Em sentido horário a partir do canto noroeste. A ordem dos vértices não muda
 * a área, mas muda o desenho quando o polígono é renderizado como linha — e
 * uma ordem estável torna o resultado conferível.
 */
export function rectangleToPolygon(a: LatLng, b: LatLng): LatLng[] {
  const [lat1, lon1] = a;
  const [lat2, lon2] = b;
  const norte = Math.max(lat1, lat2);
  const sul = Math.min(lat1, lat2);
  const oeste = Math.min(lon1, lon2);
  const leste = Math.max(lon1, lon2);
  return [
    [norte, oeste],
    [norte, leste],
    [sul, leste],
    [sul, oeste],
  ];
}

/**
 * Distância em metros entre dois pontos (Haversine).
 *
 * Serve para o raio do círculo enquanto se arrasta o mouse. É a mesma fórmula
 * que o processor usa em `geo.ts` — redeclarada porque a fronteira entre
 * serviços não é import, e porque são oito linhas.
 */
export function metersBetween(a: LatLng, b: LatLng): number {
  const R = 6_371_000;
  const rad = Math.PI / 180;
  const dLat = (b[0] - a[0]) * rad;
  const dLon = (b[1] - a[1]) * rad;
  const lat1 = a[0] * rad;
  const lat2 = b[0] * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.min(1, Math.sqrt(h))));
}

/**
 * O CENTROIDE de um polígono, ponderado por área — onde a etiqueta vai.
 *
 * A média simples dos vértices (`centerOf`) serve para enquadrar o mapa e NÃO
 * serve para posicionar texto: ela puxa o ponto para o lado que tem mais
 * vértices. Num retângulo com um canto recortado em dez cliques, a média cai
 * perto do recorte, e a etiqueta sai do meio da cerca.
 *
 * Com menos de três pontos não há área, e aí a média é a resposta certa — é o
 * ponto médio de um segmento.
 *
 * **Ressalva honesta:** num polígono fortemente côncavo (um U), o centroide de
 * área cai no vão, fora da cerca. Resolver isso de verdade é o "polo de
 * inacessibilidade", que é busca iterativa — caro demais para posicionar um
 * rótulo. Para retângulo, círculo e os pátios que esta frota cadastra, o
 * centroide acerta.
 */
export function centroidOf(points: readonly LatLng[]): LatLng | null {
  if (points.length === 0) return null;
  if (points.length < 3) return centerOf(points);

  // TRANSLADA para o primeiro vértice antes de somar, e isto não é zelo: os
  // produtos cruzados usam a coordenada ABSOLUTA (~37 de longitude) para
  // extrair diferenças de ~0,01, e a subtração de dois números grandes quase
  // iguais perde os dígitos que importam. Medido: sem transladar, o centroide
  // de um retângulo errava 3×10⁻⁹ grau — irrelevante em metros, mas é
  // cancelamento catastrófico acontecendo, e num polígono de mil vértices ele
  // não fica pequeno.
  const [lat0, lon0] = points[0]!;
  let areaDobro = 0;
  let somaLat = 0;
  let somaLon = 0;
  for (let i = 0; i < points.length; i++) {
    const lat1 = points[i]![0] - lat0;
    const lon1 = points[i]![1] - lon0;
    const proximo = points[(i + 1) % points.length]!;
    const lat2 = proximo[0] - lat0;
    const lon2 = proximo[1] - lon0;
    const cruz = lon1 * lat2 - lon2 * lat1;
    areaDobro += cruz;
    somaLat += (lat1 + lat2) * cruz;
    somaLon += (lon1 + lon2) * cruz;
  }
  // Área zero: vértices colineares, ou todos no mesmo lugar. A fórmula
  // dividiria por zero e devolveria `NaN`, que no Leaflet vira cerca sem
  // etiqueta e nenhum erro.
  if (areaDobro === 0) return centerOf(points);
  return [lat0 + somaLat / (3 * areaDobro), lon0 + somaLon / (3 * areaDobro)];
}

/**
 * O ponto no MEIO do caminho de uma polilinha, medido por comprimento.
 *
 * Para a etiqueta da cerca de linha. A média dos vértices (`centerOf`) cairia
 * fora do traçado num corredor em L — a etiqueta flutuaria ao lado da via, e
 * quem olha o mapa não saberia de qual das duas ela é.
 *
 * Mede em graus, não em metros: a proporção é a mesma, e a diferença entre as
 * duas só importaria num traçado que atravessasse latitudes muito diferentes —
 * o que um trecho de via não faz.
 */
export function midpointOfPath(points: readonly LatLng[]): LatLng | null {
  if (points.length === 0) return null;
  if (points.length === 1) return points[0]!;

  const trechos: number[] = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const [lat1, lon1] = points[i - 1]!;
    const [lat2, lon2] = points[i]!;
    const d = Math.hypot(lat2 - lat1, lon2 - lon1);
    trechos.push(d);
    total += d;
  }
  if (total === 0) return points[0]!;

  let andado = 0;
  for (let i = 0; i < trechos.length; i++) {
    const d = trechos[i]!;
    if (andado + d >= total / 2) {
      const t = d === 0 ? 0 : (total / 2 - andado) / d;
      const [lat1, lon1] = points[i]!;
      const [lat2, lon2] = points[i + 1]!;
      return [lat1 + (lat2 - lat1) * t, lon1 + (lon2 - lon1) * t];
    }
    andado += d;
  }
  return points.at(-1)!;
}

/** O centro de um conjunto de pontos — para enquadrar o mapa, não para área. */
export function centerOf(points: readonly LatLng[]): LatLng | null {
  if (points.length === 0) return null;
  const lat = points.reduce((acc, p) => acc + p[0], 0) / points.length;
  const lon = points.reduce((acc, p) => acc + p[1], 0) / points.length;
  return [lat, lon];
}

export interface DrawnShape {
  kind: "circle" | "polygon" | "line";
  center: LatLng | null;
  radiusM: number | null;
  points: LatLng[];
  /**
   * Largura TOTAL da faixa, em metros — só na linha (migration 0028).
   *
   * Vem do FORMULÁRIO, não do mapa: largura não se desenha com clique, se
   * digita. Por isso ela mora no `DrawnShape` e não no modo de desenho.
   */
  widthM: number | null;
}

/** Largura padrão da faixa, em metros. Uma pista de ida e volta com acostamento. */
export const DEFAULT_WIDTH_M = 20;

/**
 * O desenho está pronto para salvar?
 *
 * Devolve a razão em português quando não está — a tela mostra essa frase em
 * vez de um botão desabilitado sem explicação, que é o que faz alguém clicar
 * três vezes e desistir.
 */
export function shapeProblem(shape: DrawnShape): string | null {
  if (shape.kind === "circle") {
    if (!shape.center) return "Clique no mapa para marcar o centro.";
    if (!shape.radiusM || shape.radiusM < 10) return "O raio precisa de pelo menos 10 m.";
    if (shape.radiusM > 500_000) return "O raio passa de 500 km — confira o ponto.";
    return null;
  }
  if (shape.kind === "line") {
    // DOIS pontos bastam: um trecho reto de via é uma cerca legítima, e exigir
    // três recusaria o caso mais comum. Os limites da largura são os mesmos que
    // a API aplica — ver `geofences.dto.ts`.
    if (shape.points.length < 2) return "Clique ao longo da via: a linha precisa de pelo menos 2 pontos.";
    if (!shape.widthM || shape.widthM < 5) return "A largura da faixa precisa de pelo menos 5 m.";
    if (shape.widthM > 5_000) return "A largura passa de 5 km — para uma área use polígono.";
    return null;
  }
  if (shape.points.length < 3) return "Um polígono precisa de pelo menos 3 pontos.";
  return null;
}

/**
 * Metros por pixel na tela, para desenhar a faixa com a LARGURA de verdade.
 *
 * A espessura de traço do Leaflet é em PIXELS, e a largura da cerca é em
 * metros: sem converter, uma faixa de 20 m desenharia igual a uma de 200 m, e o
 * operador não teria como conferir o que digitou. A conversão depende do zoom e
 * da latitude — é a fórmula do tile do Web Mercator.
 */
export function metersPerPixel(lat: number, zoom: number): number {
  return (156_543.033_92 * Math.cos((lat * Math.PI) / 180)) / 2 ** zoom;
}

/**
 * Espessura de traço, em pixels, para uma faixa de `widthM` metros.
 *
 * O piso de 2 px é para a cerca não sumir quando o mapa está afastado: uma
 * faixa de 20 m em zoom de cidade daria menos de meio pixel, e uma cerca
 * invisível parece uma cerca que não existe.
 */
export function strokeForWidth(widthM: number, lat: number, zoom: number): number {
  return Math.max(2, widthM / metersPerPixel(lat, zoom));
}
